import type { DriverEvent } from '@kmdx/contracts';
import { fromE7 } from '@kmdx/contracts';
import { create } from 'zustand';
import {
  ApiError,
  completeDocument,
  fetchBootstrap,
  initDocument,
  OfflineError,
  postEvents,
  postExpense,
  postMessage,
  postVehicleCheck,
  uploadBlob,
} from './api';
import { db, metaGet, metaSet } from './db';
import { markFailed, markSent, pruneSentItems, queueSummary } from './outbox';
import { readyToSend } from './outbox-order';
import { navigate } from './router';
import { requestBackgroundSync } from './service-worker';
import { isUnlocked } from '../state/session';
import { useSettings } from '../state/settings';
import type { OutboxItem, PhotoRecord, StopDto, TripDto } from './types';

/**
 * Двигателят на синхронизацията. Правилата са три:
 * 1. Нищо не се губи — записът е в Dexie, преди мрежата да бъде докосната.
 * 2. Повторното изпращане е безопасно — всеки ред носи ключ от устройството.
 * 3. Шофьорът винаги вижда какво още не е тръгнало.
 */

interface SyncState {
  online: boolean;
  running: boolean;
  pending: number;
  blocked: number;
  photosPending: number;
  lastSyncAt: string | null;
  lastError: string | null;
}

export const useSyncState = create<SyncState>(() => ({
  online: navigator.onLine,
  running: false,
  pending: 0,
  blocked: 0,
  photosPending: 0,
  lastSyncAt: null,
  lastError: null,
}));

const EVENT_BATCH = 50;
const TICK_MS = 20_000;

let timer: number | null = null;
let inFlight: Promise<void> | null = null;
let rerunRequested = false;

/** ─── Публично ────────────────────────────────────────────────────────── */

export function startSync(): void {
  if (timer !== null) return;

  const online = () => {
    useSyncState.setState({ online: true });
    requestFlush();
  };
  const offline = () => useSyncState.setState({ online: false });

  window.addEventListener('online', online);
  window.addEventListener('offline', offline);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') requestFlush();
  });
  navigator.serviceWorker?.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string; url?: string } | null;
    if (data?.type === 'kmdx-flush') requestFlush();
    if (data?.type === 'kmdx-navigate' && data.url?.startsWith('/')) navigate(data.url);
  });

  timer = window.setInterval(() => void flush(), TICK_MS);
  void refreshCounters();
  requestFlush();
}

/** Мек подтик: изпраща, ако може, но никога не блокира интерфейса. */
export function requestFlush(): void {
  void flush();
  void requestBackgroundSync();
}

/** Бутонът „Синхронизирай сега" — същият път, но с видима грешка при провал. */
export async function flushNow(): Promise<void> {
  await pullTrips();
  await flush();
}

export async function refreshCounters(): Promise<void> {
  const summary = await queueSummary();
  useSyncState.setState({
    pending: summary.pending,
    blocked: summary.blocked,
    photosPending: summary.photos,
  });
}

/** ─── Двигателят ──────────────────────────────────────────────────────── */

async function flush(): Promise<void> {
  if (inFlight) {
    rerunRequested = true;
    return inFlight;
  }
  inFlight = runFlush().finally(() => {
    inFlight = null;
    if (rerunRequested) {
      rerunRequested = false;
      void flush();
    }
  });
  return inFlight;
}

async function runFlush(): Promise<void> {
  if (!navigator.onLine || !isUnlocked()) {
    await refreshCounters();
    return;
  }

  useSyncState.setState({ running: true });
  try {
    // Прекъснат по средата опит (убит раздел, изгаснал телефон) остава в
    // състояние „изпраща се" завинаги, ако не се върне обратно в опашката.
    await recoverStuckItems();

    const now = Date.now();
    const eligible = readyToSend(await db.outbox.where('status').equals('pending').toArray(), now);

    await sendEventBatch(eligible.filter((item) => item.kind === 'event'));

    for (const item of eligible.filter((item) => item.kind !== 'event')) {
      if (item.kind === 'photo' && !mayUploadPhotos()) continue;
      await sendOne(item);
    }

    await pruneSentItems();
    useSyncState.setState({ lastError: null });
    await metaSet('sync.lastFlushAt', new Date().toISOString());
    useSyncState.setState({ lastSyncAt: new Date().toISOString() });
  } catch (error) {
    useSyncState.setState({ lastError: describe(error) });
  } finally {
    useSyncState.setState({ running: false });
    await refreshCounters();
  }
}

/**
 * Изпращането е сериализирано (`inFlight`), затова всеки ред в състояние
 * „изпраща се" в началото на нов кръг е останал от предишен, прекъснат —
 * връща се в опашката без проверки за време.
 */
async function recoverStuckItems(): Promise<void> {
  const stuck = await db.outbox.where('status').equals('sending').toArray();
  for (const item of stuck) {
    await db.outbox.update(item.id, { status: 'pending', nextAttemptAt: 0 });
    if (item.kind === 'photo') await db.photos.update(item.refId, { status: 'queued', progress: 0 });
  }
}

/**
 * Снимките са тежки. Ако шофьорът е забранил качване по мобилни данни, те
 * чакат Wi-Fi; събитията тръгват винаги — те са няколко стотин байта и
 * диспечерът има нужда точно от тях.
 */
function mayUploadPhotos(): boolean {
  if (useSettings.getState().uploadPhotosOnMobileData) return true;
  const connection = (
    navigator as Navigator & { connection?: { type?: string; effectiveType?: string } }
  ).connection;
  if (!connection?.type) return true; // няма как да се разбере — качваме
  return connection.type === 'wifi' || connection.type === 'ethernet';
}

async function sendEventBatch(items: OutboxItem[]): Promise<void> {
  if (items.length === 0) return;
  const batch = items.slice(0, EVENT_BATCH);
  const records = await db.events.bulkGet(batch.map((item) => item.refId));

  const payload: DriverEvent[] = [];
  const sending: OutboxItem[] = [];
  for (let i = 0; i < batch.length; i++) {
    const item = batch[i];
    const record = records[i];
    if (!item) continue;
    if (!record) {
      // Записът е изчезнал (изтрита база) — редът в опашката няма какво да прати.
      await db.outbox.delete(item.id);
      continue;
    }
    sending.push(item);
    payload.push({
      clientEventId: record.id,
      tripId: record.tripId,
      ...(record.orderStopId ? { orderStopId: record.orderStopId } : {}),
      type: record.type,
      at: record.at,
      ...(record.latE7 !== null ? { lat: fromE7(record.latE7) } : {}),
      ...(record.lonE7 !== null ? { lon: fromE7(record.lonE7) } : {}),
      ...(record.accuracyM !== null ? { accuracyM: record.accuracyM } : {}),
      ...(record.note ? { note: record.note } : {}),
      ...(record.payload ? { payload: record.payload } : {}),
    });
  }
  if (payload.length === 0) return;

  try {
    /**
     * Сървърът казва кои събития е приел. Непотвърденото (още се обработва от
     * друга заявка) остава в опашката и тръгва пак — иначе би се изгубило.
     */
    const result = await postEvents(payload);
    const accepted = new Set(result.accepted);
    for (const item of sending) {
      if (accepted.has(item.refId)) {
        await markSent(item.id);
        await db.events.update(item.refId, { synced: 1 });
      } else {
        await markFailed(item.id, 'Сървърът още обработва събитието', false);
      }
    }
  } catch (error) {
    const permanent = error instanceof ApiError && error.permanent;
    for (const item of sending) await markFailed(item.id, describe(error), permanent);
  }
}

async function sendOne(item: OutboxItem): Promise<void> {
  try {
    await db.outbox.update(item.id, { status: 'sending' });
    switch (item.kind) {
      case 'photo':
        await sendPhoto(item.refId);
        break;
      case 'expense':
        await sendExpense(item.refId);
        break;
      case 'message':
        await sendMessageItem(item.refId);
        break;
      case 'vehicle_check':
        await sendVehicleCheck(item.refId);
        break;
      default:
        break;
    }
    await markSent(item.id);
  } catch (error) {
    const permanent = error instanceof ApiError && error.permanent;
    await markFailed(item.id, describe(error), permanent);
    if (item.kind === 'photo') {
      // Временният отказ не е грешка за шофьора — снимката просто чака следващия
      // опит. „Грешка" се показва само когато повторният опит няма да помогне.
      await db.photos.update(item.refId, {
        status: permanent ? 'error' : 'queued',
        progress: 0,
        error: permanent ? describe(error) : null,
      });
    }
  }
}

async function sendPhoto(id: string): Promise<void> {
  const photo = await db.photos.get(id);
  if (!photo) return;
  if (!photo.blob) {
    // Файлът вече е качен и изчистен от диска — няма какво да се прати повторно.
    await db.photos.update(id, { status: 'done', progress: 1 });
    return;
  }

  await db.photos.update(id, { status: 'uploading', progress: 0, error: null });

  const init = await initDocument({
    clientDocId: photo.id,
    type: photo.type,
    ...(photo.tripId ? { tripId: photo.tripId } : {}),
    ...(photo.orderStopId ? { orderStopId: photo.orderStopId } : {}),
    fileName: photo.fileName,
    mimeType: photo.mimeType,
    sizeBytes: photo.sizeBytes,
    sha256: photo.sha256,
    capturedAt: photo.capturedAt,
    ...(photo.latE7 !== null ? { lat: fromE7(photo.latE7) } : {}),
    ...(photo.lonE7 !== null ? { lon: fromE7(photo.lonE7) } : {}),
    ...(photo.accuracyM !== null ? { accuracyM: photo.accuracyM } : {}),
    ...(photo.confirmedAt ? { confirmedAt: photo.confirmedAt } : {}),
    ...(photo.replacesDocumentId ? { replacesDocumentId: photo.replacesDocumentId } : {}),
    ...(photo.note ? { notes: photo.note } : {}),
  });

  if (init.upload) {
    await uploadBlob(init.upload, photo.blob, (fraction) => {
      void db.photos.update(id, { progress: fraction });
    });
    await completeDocument(photo.id);
  }
  await db.photos.update(id, { status: 'done', progress: 1, error: null });
}

async function sendExpense(id: string): Promise<void> {
  const expense = await db.expenses.get(id);
  if (!expense) return;
  await postExpense({
    clientExpenseId: expense.id,
    tripId: expense.tripId,
    kind: expense.kind,
    amountMinor: expense.amountMinor,
    ccy: expense.ccy,
    country: expense.country,
    spentAt: expense.spentAt,
    supplier: expense.supplier,
    litresX100: expense.litresX100,
    odometerKm: expense.odometerKm,
    description: expense.note,
    clientDocId: expense.receiptDocId,
  });
  await db.expenses.update(id, { synced: 1 });
}

async function sendMessageItem(id: string): Promise<void> {
  const message = await db.messages.get(id);
  if (!message) return;
  await postMessage({
    clientMessageId: message.id,
    tripId: message.tripId,
    body: message.body,
    sentAt: message.sentAt,
  });
  await db.messages.update(id, { pending: 0 });
}

async function sendVehicleCheck(id: string): Promise<void> {
  const check = await db.checks.get(id);
  if (!check) return;
  await postVehicleCheck({
    clientCheckId: check.id,
    tripId: check.tripId,
    vehicleId: check.vehicleId,
    at: check.at,
    odometerKm: check.odometerKm,
    items: check.items,
    problem: check.problem,
  });
  await db.checks.update(id, { synced: 1 });
}

/** ─── Изтегляне на курсовете ──────────────────────────────────────────── */

export async function pullTrips(): Promise<void> {
  if (!navigator.onLine || !isUnlocked()) return;
  try {
    const since = await metaGet<string>('sync.serverTime');
    const data = await fetchBootstrap(since);

    await db.transaction('rw', [db.trips, db.stops, db.messages, db.events, db.photos, db.tasks], async () => {
      for (const trip of data.trips) await writeTrip(trip);
      /**
       * Курс, който сървърът вече не праща (отказан, преназначен, върнат в
       * плана), изчезва от телефона — иначе стои като „чака потвърждение"
       * завинаги и крие следващия. Само ако няма неизпратени действия по него.
       */
      const current = new Set(data.trips.map((t) => t.id));
      for (const local of await db.trips.toArray()) {
        if (current.has(local.id)) continue;
        const pending = await db.events.where('tripId').equals(local.id).filter((e) => e.synced === 0).count();
        if (pending > 0) continue;
        await db.stops.where('tripId').equals(local.id).delete();
        await db.trips.delete(local.id);
      }
      for (const message of data.messages) {
        const existing = await db.messages.get(message.id);
        if (!existing || existing.pending === 0) await db.messages.put({ ...message, pending: 0 });
      }
      /**
       * Решенията на офиса за снимките — „приета", „върната". Задачите
       * „снимай пак" се подменят изцяло: сървърът праща само отворените.
       */
      for (const state of data.documents ?? []) {
        if (!state.clientDocId) continue;
        const local = await db.photos.get(state.clientDocId);
        if (local && (local.serverStatus !== state.status || local.rejectReason !== state.rejectReason)) {
          await db.photos.update(state.clientDocId, { serverStatus: state.status, rejectReason: state.rejectReason });
        }
      }
      if (data.tasks) {
        await db.tasks.clear();
        if (data.tasks.length > 0) await db.tasks.bulkPut(data.tasks);
      }
    });

    await metaSet('sync.serverTime', data.serverTime);
    await metaSet('auth.driver', data.driver);
    useSyncState.setState({ lastSyncAt: new Date().toISOString(), lastError: null });
  } catch (error) {
    useSyncState.setState({ lastError: describe(error) });
  }
}

async function writeTrip(trip: TripDto): Promise<void> {
  const { stops, ...rest } = trip;
  await db.trips.put({ ...rest, updatedAt: new Date().toISOString() });

  for (const stop of stops) await writeStop(stop);

  // Адреси, паднали от курса (диспечерът е пренаредил), не бива да останат.
  const known = new Set(stops.map((stop) => stop.id));
  const local = await db.stops.where('tripId').equals(trip.id).toArray();
  const orphans = local.filter((stop) => !known.has(stop.id)).map((stop) => stop.id);
  if (orphans.length > 0) await db.stops.bulkDelete(orphans);
}

/**
 * Сървърът е автор на адреса, часа и изискваните документи. Състоянието на
 * адреса обаче може да е по-напред на телефона — шофьорът е натиснал
 * „пристигнах" в тунел. Докато събитието не е потвърдено, локалното състояние
 * печели, иначе екранът се връща назад пред очите му.
 */
async function writeStop(stop: StopDto): Promise<void> {
  const local = await db.stops.get(stop.id);
  const events = await db.events.where('orderStopId').equals(stop.id).toArray();
  const unsynced = events.filter((e) => e.synced === 0).length;
  let status = local && unsynced > 0 ? local.status : stop.status;
  /**
   * Разтоварването е „доставено" за сървъра, но на телефона адресът остава
   * отворен до подписа на получателя — оттам е единственият път към екрана
   * за подпис. Иначе следващото изтегляне скрива стъпката „Подпис".
   */
  if (
    stop.kind === 'delivery' &&
    status === 'completed' &&
    events.some((e) => e.type === 'unloaded') &&
    !events.some((e) => e.type === 'pod_captured')
  ) {
    status = 'arrived';
  }
  await db.stops.put({ ...stop, status });
}

/** ─── Помощни ─────────────────────────────────────────────────────────── */

function describe(error: unknown): string {
  if (error instanceof OfflineError) return 'Няма връзка';
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Неочаквана грешка';
}

export function photoProgressText(photo: PhotoRecord): string {
  switch (photo.status) {
    case 'done':
      return 'Изпратена';
    case 'uploading':
      return `Изпраща се… ${Math.round(photo.progress * 100)}%`;
    case 'error':
      return photo.error ?? 'Грешка';
    default:
      return 'Чака връзка';
  }
}
